import { X, Tag, CheckCircle2, Percent, Copy, Check } from 'lucide-react';
import { useState } from 'react';

import { useAppSelector } from '../../redux/store/hooks';
import {
  useGetCoupnsQuery,
  useValidateCouponMutation
} from '../../services/payments';

type PaymentCoupnsProps = {
  setShowCoupnsOverlay: any;
  amount: number;
  setCouponCode: any;
};

const PaymentCoupns = ({
  setShowCoupnsOverlay,
  amount,
  setCouponCode
}: PaymentCoupnsProps) => {
  const { data, isLoading } = useGetCoupnsQuery({});
  const [selectedCoupon, setSelectedCoupon] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const coupnsData = useAppSelector(
    state => state.root.payments.paymentCoupnsData
  );

  const [validateCoupon] = useValidateCouponMutation();

  const userData = localStorage.getItem('dashboards');
  const userDashboards: number[] = userData
    ? userData.split(',').map(Number)
    : [];

  const handleApplyCoupon = (couponCode: string) => {
    setSelectedCoupon(couponCode);
    // Add your coupon application logic here
  };

  const handleContinueWithCoupon = async () => {
    const body = {
      code: selectedCoupon,
      minimumPurchasedOrder: amount,
      dashboards: userDashboards
    };
    try {
      await validateCoupon(body).unwrap();
      setCouponCode(selectedCoupon ?? '');
      setShowCoupnsOverlay(false);
    } catch (error) {
      console.log(error, 'error67');
    }
  };

  const copyToClipboard = (e: any, couponCode: string) => {
    navigator.clipboard.writeText(couponCode);
    setCopiedCode(couponCode);
    setTimeout(() => {
      setCopiedCode(null);
    }, 2000);
  };

  return (
    <div className='fixed inset-0 z-[55] flex items-center justify-end bg-black bg-opacity-50'>
      <div className='animate-slideIn h-full w-full max-w-md overflow-y-auto bg-gray-50 shadow-2xl'>
        {/* Header */}
        <div className='sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-6'>
          <div className='flex items-center space-x-3'>
            <div className='rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 p-2'>
              <Percent className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-2xl font-bold text-gray-900'>
                Available Coupons
              </h2>
              <p className='text-sm text-gray-500'>
                Save more on your purchase
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCoupnsOverlay(false)}
            className='rounded-lg p-2 transition-colors hover:bg-gray-100'
            aria-label='Close'
          >
            <X className='h-6 w-6 text-gray-600' />
          </button>
        </div>

        {/* Content */}
        <div className='space-y-4 p-6'>
          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='h-8 w-8 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600'></div>
            </div>
          ) : coupnsData && coupnsData.length > 0 ? (
            coupnsData.map((each, index) => {
              const isSelected = selectedCoupon === each.couponCode;
              const isCopied = copiedCode === each.couponCode;
              return (
                <div
                  key={each.couponCode}
                  className={`group cursor-pointer rounded-xl border p-5 shadow-sm transition-all ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                  }`}
                >
                  <div className='mb-3 flex items-start justify-between'>
                    <div className='flex items-center space-x-2'>
                      <div
                        className={`rounded-lg p-1.5 ${
                          isSelected ? 'bg-purple-100' : 'bg-purple-50'
                        }`}
                      >
                        <Tag
                          className={`h-4 w-4 ${
                            isSelected ? 'text-purple-700' : 'text-purple-600'
                          }`}
                        />
                      </div>
                      <h3 className='text-lg font-bold text-gray-900'>
                        {each.couponName}
                      </h3>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className='h-5 w-5 text-green-600' />
                    )}
                  </div>

                  <div className='mb-3 flex items-center space-x-2'>
                    <span className='text-xs font-medium uppercase text-gray-500'>
                      Code:
                    </span>
                    <code
                      className={`rounded-md px-3 py-1.5 text-sm font-bold tracking-wide ${
                        isSelected
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-purple-50 text-purple-700'
                      }`}
                    >
                      {each.couponCode}
                    </code>
                    <button
                      onClick={e => copyToClipboard(e, each.couponCode)}
                      className='transition-transform hover:scale-110'
                      aria-label='Copy coupon code'
                    >
                      {isCopied ? (
                        <Check className='h-5 w-5 text-green-600' />
                      ) : (
                        <Copy className='h-5 w-5 text-gray-600 hover:text-purple-600' />
                      )}
                    </button>
                  </div>

                  <p className='text-sm leading-relaxed text-gray-600'>
                    {each.description}
                  </p>

                  {/* <div className='mt-4 border-t border-gray-100 pt-3'>
                    <button
                      className={`text-sm font-semibold transition-colors ${
                        isSelected
                          ? 'text-purple-700 hover:text-purple-800'
                          : 'text-purple-600 hover:text-purple-700'
                      }`}
                    >
                      {isSelected ? 'Applied' : 'Apply Coupon'}
                    </button>
                  </div> */}
                </div>
              );
            })
          ) : (
            <div className='flex flex-col items-center justify-center py-16'>
              <div className='mb-4 rounded-full bg-gray-100 p-6'>
                <Tag className='h-10 w-10 text-gray-400' />
              </div>
              <p className='text-base font-medium text-gray-700'>
                No coupons available
              </p>
              <p className='mt-1 text-sm text-gray-500'>
                Check back later for exciting offers
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {selectedCoupon && (
          <div className='sticky bottom-0 border-t border-gray-200 bg-white p-6'>
            <button
              onClick={handleContinueWithCoupon}
              className='w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-3.5 font-semibold text-white shadow-md transition-all hover:from-purple-700 hover:to-purple-800 hover:shadow-lg active:scale-[0.98]'
            >
              Continue with Coupon
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCoupns;
